import { useNavigate } from 'react-router-dom';
import { ClipboardList, Camera, FilePlus2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { useAppShell } from './AppShellContext';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

/**
 * Global Capture sheet, opened from the bottom nav's primary button.
 * Three creation affordances that don't have a dedicated bottom-nav
 * cell of their own: a log entry, a photo, and a brand-new checklist
 * template. (Starting a run from an existing template lives on the
 * Checklists tab itself, so it isn't repeated here.)
 */
export default function CaptureSheet() {
  const { captureOpen, closeCapture, openLogForm, openPhotoSheet } = useAppShell();
  const { role } = useAuth();
  const canCreateTemplate = role === 'owner' || role === 'admin';
  const navigate = useNavigate();

  return (
    <Sheet open={captureOpen} onOpenChange={(o) => (o ? null : closeCapture())}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>Capture</SheetTitle>
          <SheetDescription>
            {canCreateTemplate
              ? 'Log work, drop a photo, or write a new checklist template.'
              : 'Log work or drop a photo.'}
          </SheetDescription>
        </SheetHeader>

        <div
          className={cn(
            'grid gap-3 pt-4 pb-2',
            canCreateTemplate ? 'grid-cols-3' : 'grid-cols-2',
          )}
          data-testid="capture-tiles"
        >
          <CaptureTile
            Icon={ClipboardList}
            label="Add log"
            onClick={() => {
              closeCapture();
              openLogForm();
            }}
          />
          <CaptureTile
            Icon={Camera}
            label="Add photo"
            onClick={() => {
              closeCapture();
              openPhotoSheet();
            }}
          />
          {canCreateTemplate ? (
            <CaptureTile
              Icon={FilePlus2}
              label="New checklist"
              onClick={() => {
                closeCapture();
                navigate('/checklists/templates/new');
              }}
              data-testid="capture-new-checklist"
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CaptureTile({
  Icon,
  label,
  hint,
  onClick,
  ...rest
}: {
  Icon: typeof ClipboardList;
  label: string;
  hint?: string;
  onClick?: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const enabled = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-lg border px-3 py-5 transition-colors',
        enabled
          ? 'border-rule text-foreground hover:border-primary/40 hover:bg-accent/30'
          : 'border-dashed border-rule text-foreground/60 cursor-not-allowed',
      )}
      {...rest}
    >
      <Icon className="h-6 w-6" aria-hidden />
      <span className="text-sm font-medium">{label}</span>
      {hint ? (
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </button>
  );
}
